import type { ReactNode } from 'react';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import LightBulb from '@repo/assets/icons/lightbulb.svg?react';
import triangleWarningUrl from '@repo/assets/icons/triangle-warning.svg?url';
import { colorPalette } from '@repo/mui-config/color-palette';
import type { FormNotifications } from '@repo/shared/types';

import { FieldNotificationWrapper } from './field-notification-wrapper';

const iconSize = {
  height: 32,
  width: 32,
};

function NotificationTypography({ children }: { children: ReactNode }) {
  return (
    <Typography lineHeight="14px" variant="subtitle1">
      {children}
    </Typography>
  );
}

export function DefaultFieldsNotification() {
  const { t } = useTranslation();

  return (
    <FieldNotificationWrapper variant="default">
      <LightBulb fill={colorPalette.primary.main} {...iconSize} />
      <NotificationTypography>
        {t('components.recipientNotification.fieldsNotification.default')}
      </NotificationTypography>
    </FieldNotificationWrapper>
  );
}

interface WarningFieldsNotificationProps {
  isPhoneFieldEmpty: boolean;
  isMailFieldEmpty: boolean;
}

export function WarningFieldsNotification({
  isPhoneFieldEmpty,
  isMailFieldEmpty,
}: WarningFieldsNotificationProps) {
  const { t } = useTranslation();

  return (
    <FieldNotificationWrapper variant="warning">
      <LightBulb fill={colorPalette.warning.main} {...iconSize} />
      <Box>
        {isPhoneFieldEmpty && !isMailFieldEmpty ? (
          <NotificationTypography>
            {t('components.recipientNotification.fieldsNotification.error.phone')}
          </NotificationTypography>
        ) : null}
        {isMailFieldEmpty && !isPhoneFieldEmpty ? (
          <NotificationTypography>
            {t('components.recipientNotification.fieldsNotification.error.mail')}
          </NotificationTypography>
        ) : null}
        {isPhoneFieldEmpty && isMailFieldEmpty ? (
          <NotificationTypography>
            {t('components.recipientNotification.fieldsNotification.error.phoneAndMail')}
          </NotificationTypography>
        ) : null}
      </Box>
    </FieldNotificationWrapper>
  );
}

interface ErrorFieldsNotificationProps {
  isPhoneError: boolean;
  isMailError: boolean;
  isDocumentError: boolean;
  isPeselError: boolean;
}

export function ErrorFieldsNotification({
  isPhoneError,
  isMailError,
  isDocumentError,
  isPeselError,
}: ErrorFieldsNotificationProps) {
  const { t } = useTranslation();

  return (
    <FieldNotificationWrapper variant="error">
      <Box component="img" src={triangleWarningUrl} alt="triangle warning icon" />
      <Box>
        {isDocumentError || isPeselError ? (
          <NotificationTypography>
            {t('components.recipientNotification.fieldsNotification.error.polishIdOrPesel')}
          </NotificationTypography>
        ) : (
          <>
            {isPhoneError && !isMailError ? (
              <NotificationTypography>
                {t('components.recipientNotification.fieldsNotification.error.phone')}
              </NotificationTypography>
            ) : null}
            {isMailError && !isPhoneError ? (
              <NotificationTypography>
                {t('components.recipientNotification.fieldsNotification.error.mail')}
              </NotificationTypography>
            ) : null}
            {isMailError && isPhoneError ? (
              <NotificationTypography>
                {t('components.recipientNotification.fieldsNotification.error.phoneAndMail')}
              </NotificationTypography>
            ) : null}
          </>
        )}
      </Box>
    </FieldNotificationWrapper>
  );
}

interface FieldNotificationProps
  extends ErrorFieldsNotificationProps, WarningFieldsNotificationProps {
  notification: FormNotifications;
}

export function FieldNotification({
  notification,
  isPhoneFieldEmpty,
  isMailFieldEmpty,
  isPhoneError,
  isMailError,
  isDocumentError,
  isPeselError,
}: FieldNotificationProps) {
  if (notification === 'warning') {
    return (
      <WarningFieldsNotification
        isPhoneFieldEmpty={isPhoneFieldEmpty}
        isMailFieldEmpty={isMailFieldEmpty}
      />
    );
  }

  if (notification === 'error') {
    return (
      <ErrorFieldsNotification
        isPhoneError={isPhoneError}
        isMailError={isMailError}
        isDocumentError={isDocumentError}
        isPeselError={isPeselError}
      />
    );
  }

  return <DefaultFieldsNotification />;
}
